/** Strings of the accounting document screens (list, detail, send-filing dialog, data box, cashflow chart): `accDocumentsEn` is the source, `accDocumentsCs` must have the same keys. */
export const accDocumentsEn = {
  accDocuments: {
    status: {
      paid: "Paid",
      partially_paid: "Partially paid",
      partially_paid_short: "Part paid",
      issued: "Issued",
      draft: "Draft",
      cancelled: "Cancelled",
    },
    badge: {
      imported: "imported",
      posted: "posted",
      overdue: "Overdue",
      testEnvironment: "test environment",
    },
    cashflow: {
      income: "Income",
      expenses: "Expenses",
      difference: "Difference",
      empty: "No documents yet.",
    },
    list: {
      columns: {
        number: "Number",
        partner: "Partner",
        issued: "Issued",
        due: "Due",
        total: "Total",
        remaining: "Remaining",
        status: "Status",
      },
      vs: "VS {{vs}}",
      pdf: "PDF",
      noPdf: "no PDF",
      filter: "Filter",
      tabs: {
        all: "All",
        unpaid: "Unpaid",
        overdue: "Overdue",
        paid: "Paid",
      },
      searchPlaceholder: "Number, partner or variable symbol…",
      summary: {
        documents: "Documents",
        totalBilled: "Total billed",
        stillOutstanding: "Still outstanding",
      },
    },
    detail: {
      untitled: "Untitled document",
      pdf: "PDF",
      isdoc: "ISDOC",
      total: "Total",
      withoutVat: "Without VAT",
      paid: "Paid",
      remaining: "Remaining",
      settled: "Settled",
      lines: "Lines",
      description: "Description",
      qty: "Qty",
      unitPrice: "Unit price",
      vat: "VAT",
      noLines: "This document has no lines — an import that only carried the totals.",
      baseAt: "Base at {{rate}} %",
      vatTotal: "VAT total",
      customer: "Customer",
      supplier: "Supplier",
      name: "Name",
      registrationNo: "Registration no.",
      vatNo: "VAT no.",
      address: "Address",
      hubCustomer: "Hub customer",
      datesAndSymbols: "Dates and symbols",
      issued: "Issued",
      taxableSupply: "Taxable supply",
      due: "Due",
      variableSymbol: "Variable symbol",
      currency: "Currency",
      payments: "Payments",
      noPayments: "Nothing has come in against this document yet.",
      date: "Date",
      amount: "Amount",
      inJournal: "In the journal",
      notPosted:
        "Not posted yet. Until it is, the document is still editable and counts in no statement.",
      journalEntryNo: "No. {{number}}",
      openJournal: "open journal",
      corrections: "Corrections",
      correctsDocument: "This document corrects",
      correctedBy: "Corrected by",
      vatMode: {
        standard: "Domestic VAT",
        reverse_charge: "Reverse charge: we assess the VAT",
        exempt: "Exempt",
        outside_scope: "Outside VAT",
        eu_supply: "EU supply",
        export: "Export",
      },
      vatDeducted: "The VAT is deducted in the VAT return.",
      vatNotDeducted: "The VAT is not deducted: it is part of the cost.",
      dontDeductVat: "Don't deduct the VAT",
      deductVat: "Deduct the VAT",
      entertainmentNote: "Entertainment: not a tax deductible cost.",
      notEntertainment: "Not entertainment",
      entertainment: "Entertainment",
      changeFailed: "Couldn't change it.",
      cancelFailed: "Couldn't cancel it.",
      cancelPrompt: "Why is this expense cancelled? (duplicate, proforma…)",
      cancelExpense: "Cancel expense",
      revertFailed: "Couldn't take it back.",
      changes: "Changes",
      revisionCancelled: "Cancelled",
      revisionCorrected: "Corrected",
      takenBack: "taken back",
      takeBack: "Take back",
    },
    sendFiling: {
      prepareFailed: "Couldn't prepare the message.",
      sendFailed: "Couldn't send the message.",
      titleReturn: "Send the VAT return (DPHDP3) by data box",
      titleControl: "Send the control statement (DPHKH1) by data box",
      description:
        "This files it with the tax office. Sending is a legal act done in the company's name and can't be taken back; a mistake is corrected by another filing.",
      preparing: "Preparing…",
      to: "To",
      from: "From",
      notVerified: "not verified",
      subject: "Subject",
      attachment: "Attachment",
      fileInfo: "{{mimeType}}, {{size}} B",
      alreadySent: "Already sent as data message {{dmId}}.",
      confirm:
        "I checked the file and the recipient, and I'm filing this in the company's name. The day of filing in the file is set to today.",
      cancel: "Cancel",
      sending: "Sending…",
      send: "Send",
    },
    dataBoxCard: {
      saved: "Saved.",
      savedLoginWorks: "Saved, and the login works.",
      savedLoginFailed: "Saved, but the login failed: {{error}}",
      saveFailed: "Couldn't save the data box.",
      loginWorks: "The login works.",
      loginFailed: "The login failed.",
      searchFailed: "The search failed.",
      officeSaveFailed: "Couldn't save the tax office.",
      title: "Data box (datová schránka)",
      description:
        "Hub logs in as a <em>pověřená osoba</em> to send VAT filings and to load messages. The password is stored encrypted and never shown again. Testing the login, sending and following what was sent deliver nothing. <strong>Loading received messages does: every message waiting in the box counts as delivered the moment Hub lists it (doručení přihlášením), if the account may read it.</strong>",
      environment: "Environment",
      production: "Production (mojedatovaschranka.cz)",
      test: "Test (czebox.cz)",
      login: "Login",
      password: "Password",
      unchanged: "unchanged",
      saving: "Saving…",
      saveAndTest: "Save and test",
      testing: "Testing…",
      testLogin: "Test the login",
      box: "Box:",
      notVerifiedYet: "not verified yet",
      lastLogin: "Last login:",
      lastError: "Last error: {{error}}",
      taxOffice: "Tax office",
      filingsGoTo: "Filings go to",
      nothingCanBeSent: "Nothing can be sent until a person confirms the tax office's data box.",
      officeQueryLabel: "Box ID, IČO or office name",
      searching: "Searching…",
      find: "Find",
      noMatch: "No public data box matches.",
      useThisOne: "Use this one",
      autoFetch: "Load messages automatically every 6 hours",
      autoFetchHint:
        "Off by default. When on, a message from an office counts as delivered within hours of arriving and its deadlines start running, even if nobody looks at it in Hub. When off, received messages load only on a click; the states of what Hub sent are followed either way, since that delivers nothing.",
    },
    dataBoxPage: {
      loadFailed: "Couldn't load the messages.",
      title: "Data box",
      notConnected: "Not connected yet, see the accounting settings.",
      testEnvironmentSuffix: " · test environment",
      refreshSent: "Refresh sent",
      loading: "Loading…",
      loadMessages: "Load messages",
      confirmLoad:
        "Every message waiting in the data box counts as delivered from this moment, and its deadlines start running. Load the received messages?",
      info: "Loading received messages delivers every message waiting in the box (doručení přihlášením). Refreshing sent messages delivers nothing.",
      autoOn: "Automatic loading is on: Hub loads received messages every 6 hours.",
      autoOff: "Automatic loading is off: received messages load only on a click.",
      lastLoaded: "Last loaded",
      lastError: "Last error: {{error}}",
      receivedResult: "Received: {{added}} new of {{listed}}.",
      receivedResultPending:
        "Received: {{added}} new of {{listed}}, {{pending}} not readable by this account.",
      sentResult: "Sent: {{added}} new, {{updated}} updated.",
      messages: "Messages",
      archiveNote:
        "Attachments are kept in Hub's archive; the data box deletes messages 90 days after delivery.",
      received: "Received",
      sent: "Sent",
      noMessages: "No messages.",
      colId: "ID",
      colFrom: "From",
      colTo: "To",
      colSubject: "Subject",
      colState: "State",
      colDelivered: "Delivered",
      colFiles: "Files",
      vatLink: "VAT {{period}}/{{year}}",
      sentBy: " · sent by {{name}}",
      sentAt: "sent",
      toTheBox: "to the box",
      accepted: "accepted",
      signedMessage: "signed message (ZFO)",
    },
  },
};

export const accDocumentsCs: typeof accDocumentsEn = {
  accDocuments: {
    status: {
      paid: "Zaplaceno",
      partially_paid: "Částečně zaplaceno",
      partially_paid_short: "Část. zaplaceno",
      issued: "Vystaveno",
      draft: "Koncept",
      cancelled: "Stornováno",
    },
    badge: {
      imported: "importováno",
      posted: "zaúčtováno",
      overdue: "Po splatnosti",
      testEnvironment: "testovací prostředí",
    },
    cashflow: {
      income: "Příjmy",
      expenses: "Výdaje",
      difference: "Rozdíl",
      empty: "Zatím žádné doklady.",
    },
    list: {
      columns: {
        number: "Číslo",
        partner: "Partner",
        issued: "Vystaveno",
        due: "Splatnost",
        total: "Celkem",
        remaining: "Zbývá",
        status: "Stav",
      },
      vs: "VS {{vs}}",
      pdf: "PDF",
      noPdf: "bez PDF",
      filter: "Filtr",
      tabs: {
        all: "Vše",
        unpaid: "Neuhrazené",
        overdue: "Po splatnosti",
        paid: "Uhrazené",
      },
      searchPlaceholder: "Číslo, partner nebo variabilní symbol…",
      summary: {
        documents: "Doklady",
        totalBilled: "Celkem vyfakturováno",
        stillOutstanding: "Zbývá uhradit",
      },
    },
    detail: {
      untitled: "Doklad bez názvu",
      pdf: "PDF",
      isdoc: "ISDOC",
      total: "Celkem",
      withoutVat: "Bez DPH",
      paid: "Zaplaceno",
      remaining: "Zbývá",
      settled: "Vyrovnáno",
      lines: "Položky",
      description: "Popis",
      qty: "Množ.",
      unitPrice: "Cena za jednotku",
      vat: "DPH",
      noLines: "Tento doklad nemá žádné položky — import, který přenesl jen celkové částky.",
      baseAt: "Základ při {{rate}} %",
      vatTotal: "DPH celkem",
      customer: "Odběratel",
      supplier: "Dodavatel",
      name: "Název",
      registrationNo: "IČO",
      vatNo: "DIČ",
      address: "Adresa",
      hubCustomer: "Zákazník v Hubu",
      datesAndSymbols: "Data a symboly",
      issued: "Vystaveno",
      taxableSupply: "DUZP",
      due: "Splatnost",
      variableSymbol: "Variabilní symbol",
      currency: "Měna",
      payments: "Platby",
      noPayments: "K tomuto dokladu zatím nepřišla žádná platba.",
      date: "Datum",
      amount: "Částka",
      inJournal: "V účetním deníku",
      notPosted:
        "Zatím nezaúčtováno. Dokud není, doklad lze ještě upravovat a nezahrnuje se do žádného výkazu.",
      journalEntryNo: "Č. {{number}}",
      openJournal: "otevřít deník",
      corrections: "Opravy",
      correctsDocument: "Tento doklad opravuje",
      correctedBy: "Opraveno dokladem",
      vatMode: {
        standard: "Tuzemské DPH",
        reverse_charge: "Přenesená daňová povinnost: DPH přiznáváme my",
        exempt: "Osvobozeno",
        outside_scope: "Mimo DPH",
        eu_supply: "Dodání do EU",
        export: "Vývoz",
      },
      vatDeducted: "DPH se uplatňuje v přiznání k DPH.",
      vatNotDeducted: "DPH se neuplatňuje: je součástí nákladu.",
      dontDeductVat: "Neuplatnit DPH",
      deductVat: "Uplatnit DPH",
      entertainmentNote: "Reprezentace: daňově neuznatelný náklad.",
      notEntertainment: "Není reprezentace",
      entertainment: "Reprezentace",
      changeFailed: "Změnu se nepodařilo provést.",
      cancelFailed: "Storno se nepodařilo provést.",
      cancelPrompt: "Proč se tento výdaj stornuje? (duplicita, zálohová faktura…)",
      cancelExpense: "Stornovat výdaj",
      revertFailed: "Vrácení se nepodařilo.",
      changes: "Změny",
      revisionCancelled: "Stornováno",
      revisionCorrected: "Opraveno",
      takenBack: "vzato zpět",
      takeBack: "Vzít zpět",
    },
    sendFiling: {
      prepareFailed: "Zprávu se nepodařilo připravit.",
      sendFailed: "Zprávu se nepodařilo odeslat.",
      titleReturn: "Odeslat přiznání k DPH (DPHDP3) datovou schránkou",
      titleControl: "Odeslat kontrolní hlášení (DPHKH1) datovou schránkou",
      description:
        "Tímto podáte dokument finančnímu úřadu. Odeslání je právní úkon učiněný jménem společnosti a nelze ho vzít zpět; chyba se opravuje dalším podáním.",
      preparing: "Připravuje se…",
      to: "Komu",
      from: "Od",
      notVerified: "neověřeno",
      subject: "Předmět",
      attachment: "Příloha",
      fileInfo: "{{mimeType}}, {{size}} B",
      alreadySent: "Již odesláno jako datová zpráva {{dmId}}.",
      confirm:
        "Zkontroloval(a) jsem soubor i příjemce a podávám to jménem společnosti. Den podání v souboru se nastaví na dnešek.",
      cancel: "Zrušit",
      sending: "Odesílá se…",
      send: "Odeslat",
    },
    dataBoxCard: {
      saved: "Uloženo.",
      savedLoginWorks: "Uloženo, přihlášení funguje.",
      savedLoginFailed: "Uloženo, ale přihlášení selhalo: {{error}}",
      saveFailed: "Datovou schránku se nepodařilo uložit.",
      loginWorks: "Přihlášení funguje.",
      loginFailed: "Přihlášení selhalo.",
      searchFailed: "Hledání selhalo.",
      officeSaveFailed: "Finanční úřad se nepodařilo uložit.",
      title: "Datová schránka",
      description:
        "Hub se přihlašuje jako <em>pověřená osoba</em>, aby odesílal podání k DPH a načítal zprávy. Heslo se ukládá šifrovaně a už se nikdy nezobrazí. Test přihlášení, odesílání ani sledování odeslaných zpráv nic nedoručuje. <strong>Načítání přijatých zpráv doručuje: každá zpráva čekající ve schránce se považuje za doručenou ve chvíli, kdy ji Hub vypíše (doručení přihlášením), pokud ji účet smí číst.</strong>",
      environment: "Prostředí",
      production: "Ostré (mojedatovaschranka.cz)",
      test: "Testovací (czebox.cz)",
      login: "Přihlašovací jméno",
      password: "Heslo",
      unchanged: "beze změny",
      saving: "Ukládá se…",
      saveAndTest: "Uložit a otestovat",
      testing: "Testuje se…",
      testLogin: "Otestovat přihlášení",
      box: "Schránka:",
      notVerifiedYet: "zatím neověřeno",
      lastLogin: "Poslední přihlášení:",
      lastError: "Poslední chyba: {{error}}",
      taxOffice: "Finanční úřad",
      filingsGoTo: "Podání jdou do schránky",
      nothingCanBeSent:
        "Nic nelze odeslat, dokud člověk nepotvrdí datovou schránku finančního úřadu.",
      officeQueryLabel: "ID schránky, IČO nebo název úřadu",
      searching: "Hledá se…",
      find: "Najít",
      noMatch: "Žádná veřejná datová schránka neodpovídá.",
      useThisOne: "Použít tuto",
      autoFetch: "Načítat zprávy automaticky každých 6 hodin",
      autoFetchHint:
        "Ve výchozím stavu vypnuto. Když je zapnuto, zpráva od úřadu se považuje za doručenou během několika hodin od příchodu a začnou jí běžet lhůty, i když se na ni v Hubu nikdo nepodívá. Když je vypnuto, přijaté zprávy se načtou jen kliknutím; stavy toho, co Hub odeslal, se sledují v obou případech, protože to nic nedoručuje.",
    },
    dataBoxPage: {
      loadFailed: "Zprávy se nepodařilo načíst.",
      title: "Datová schránka",
      notConnected: "Zatím nepřipojeno, viz nastavení účetnictví.",
      testEnvironmentSuffix: " · testovací prostředí",
      refreshSent: "Obnovit odeslané",
      loading: "Načítá se…",
      loadMessages: "Načíst zprávy",
      confirmLoad:
        "Každá zpráva čekající v datové schránce se od této chvíle považuje za doručenou a začnou jí běžet lhůty. Načíst přijaté zprávy?",
      info: "Načtení přijatých zpráv doručí každou zprávu čekající ve schránce (doručení přihlášením). Obnovení odeslaných zpráv nic nedoručuje.",
      autoOn: "Automatické načítání je zapnuto: Hub načítá přijaté zprávy každých 6 hodin.",
      autoOff: "Automatické načítání je vypnuto: přijaté zprávy se načtou jen kliknutím.",
      lastLoaded: "Naposledy načteno",
      lastError: "Poslední chyba: {{error}}",
      receivedResult: "Přijaté: {{added}} nových z {{listed}}.",
      receivedResultPending:
        "Přijaté: {{added}} nových z {{listed}}, {{pending}} tento účet nesmí číst.",
      sentResult: "Odeslané: {{added}} nových, {{updated}} aktualizováno.",
      messages: "Zprávy",
      archiveNote:
        "Přílohy se uchovávají v archivu Hubu; datová schránka maže zprávy 90 dní po doručení.",
      received: "Přijaté",
      sent: "Odeslané",
      noMessages: "Žádné zprávy.",
      colId: "ID",
      colFrom: "Od",
      colTo: "Komu",
      colSubject: "Předmět",
      colState: "Stav",
      colDelivered: "Doručeno",
      colFiles: "Soubory",
      vatLink: "DPH {{period}}/{{year}}",
      sentBy: " · odeslal(a) {{name}}",
      sentAt: "odesláno",
      toTheBox: "do schránky",
      accepted: "přijato",
      signedMessage: "podepsaná zpráva (ZFO)",
    },
  },
};
